/**
 * BfAccountHub, konto-vyn på /pages/konto: favoriter-tab.
 * Ersätter den gamla assets/bf-favorites.js.
 *
 * No-op på sidor utan [data-bf-favorites-list].
 * Delar api/el/dialog/toast med bf-saved.js via window.BfSaved.
 *
 * Produktkorten länkar via product_handle och visar lagrad bild/titel
 * (kolumnerna finns i backend-schemat och fylls av spara-knapparna).
 */

import { createDebugger } from '~/scripts/utils/debug.js';
import { sized } from '../utils/img.js';
import { t } from '~/studio/lib/i18n.js';

const debug = createDebugger('bf-account-hub', false);

const S = () => window.BfSaved;

// ---------- Favoriter ----------

const HEART_SVG =
    '<svg viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="5" aria-hidden="true" focusable="false"><path d="M51.27 15.05a13 13 0 0 0-18.44 0l-.83.83-.83-.83a13 13 0 0 0-18.44 18.44l.83.83L32 52.77l18.44-18.45.83-.83a13 13 0 0 0 0-18.44Z"/></svg>';

// Samma favorithjärta som överallt annars (data-bf-fav-btn). bf-saved.js hanterar klicket
// (avmarkerar + skickar bf:favorites-changed); reconcileFavGrid tar bort kortet direkt.
function favoriteCard(f) {
    const { el } = S();
    const title = f.product_title || 'Produkt #' + f.product_id;
    const coverAttrs = {
        class: 'bf-favorite-card__cover',
        style: f.product_image
            ? `background-image:url(${JSON.stringify(sized(String(f.product_image), 500)).slice(1, -1)})`
            : ''
    };
    const cover = f.product_handle
        ? el('a', Object.assign({ href: '/products/' + f.product_handle }, coverAttrs))
        : el('div', coverAttrs);
    const heart = el('div', { class: 'bf-product-actions bf-product-actions--small bf-favorite-card__heart' }, [
        el('button', {
            class: 'bf-product-actions__btn is-favorited',
            type: 'button',
            'data-bf-fav-btn': '',
            'data-product-id': String(f.product_id),
            'data-product-handle': f.product_handle || '',
            'data-product-title': f.product_title || '',
            'data-product-image': f.product_image || '',
            'data-product-vendor': f.product_vendor || '',
            'data-product-type': f.product_type || '',
            'aria-pressed': 'true',
            'aria-label': t('hub.remove_favorite'),
            title: t('hub.remove_favorite'),
            html: HEART_SVG
        })
    ]);
    return el('div', { class: 'bf-favorite-card', 'data-fav-id': String(f.product_id) }, [
        cover,
        heart,
        el('div', { class: 'bf-favorite-card__body' }, [
            f.product_vendor ? el('p', { class: 'bf-favorite-card__vendor' }, f.product_vendor) : null,
            f.product_handle
                ? el('a', { class: 'bf-favorite-card__title', href: '/products/' + f.product_handle }, title)
                : el('p', { class: 'bf-favorite-card__title' }, title)
        ])
    ]);
}

// Ta bort kort vars produkt inte längre är favorit (efter att hjärtat avmarkerats någonstans).
function reconcileFavGrid(root) {
    if (!root) return;
    const ids = S().getFavIds();
    let remaining = 0;
    root.querySelectorAll('[data-fav-id]').forEach((card) => {
        if (ids.has(card.getAttribute('data-fav-id'))) remaining += 1;
        else card.remove();
    });
    if (remaining === 0) {
        const { el } = S();
        root.innerHTML = '';
        root.appendChild(el('p', { class: 'bf-boards__empty' }, t('hub.no_favorites')));
        setFavTabVisible(false);
    }
}

// Göm Favoriter-fliken (och översiktskortet) helt när kunden inte har några favoriter.
function setFavTabVisible(visible) {
    document
        .querySelectorAll('[data-bf-tab="favoriter"], [data-bf-tab-link="favoriter"]')
        .forEach((node) => {
            node.style.display = visible ? '' : 'none';
        });
}

async function loadFavorites(root) {
    const { api, el, toast, setFavIds, syncFavButtons } = S();
    root.innerHTML = '<p class="bf-boards__loading">' + t('hub.loading_dots') + '</p>';
    try {
        const { favorites } = await api('/api/favorites');
        setFavTabVisible(favorites.length > 0);
        setFavIds(new Set(favorites.map((f) => String(f.product_id))));
        syncFavButtons();
        root.innerHTML = '';
        if (favorites.length === 0) {
            root.appendChild(
                el(
                    'p',
                    { class: 'bf-boards__empty' },
                    t('hub.no_favorites_hint')
                )
            );
            return;
        }
        const grid = el('div', { class: 'bf-favorites__grid' });
        for (const f of favorites) grid.appendChild(favoriteCard(f));
        root.appendChild(grid);
    } catch (e) {
        root.innerHTML = '';
        root.appendChild(el('p', { class: 'bf-boards__error' }, t('hub.favorites_error', { error: e.message })));
    }
}

// ---------- init ----------

function init() {
    const favList = document.querySelector('[data-bf-favorites-list]');
    if (!favList) return;
    if (!S()) {
        debug.error('window.BfSaved saknas, bf-saved.js måste laddas först');
        return;
    }

    if (favList) loadFavorites(favList);
    // Hjärtat på favoritkorten avmarkerar via bf-saved.js; ta bort kortet direkt när det händer.
    document.addEventListener('bf:favorites-changed', () => reconcileFavGrid(favList));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
